import { useGetUtilitiesQuery } from '@/services/utilities';
import { useMemo } from 'react';

import { DataFilterOperation, PromoUtility } from 'types';

import { toUtilityOptionValue } from './promoFormModel';

export interface PromoUtilityOption {
  label: string;
  value: string;
}

/**
 * Every state that has at least one utility, plus every utility key. The state picker offers only
 * these states, and the table compares a promo against them to collapse a full selection.
 */
export const usePromoAvailableStates = (knownUtilities: PromoUtility[]) => {
  const { data, isLoading } = useGetUtilitiesQuery({});

  const { states, utilityKeys } = useMemo(() => {
    const all: PromoUtility[] = [...(data ?? []), ...knownUtilities];
    return {
      states: new Set(all.map(({ state }) => state)),
      utilityKeys: new Set(all.map(({ state, lseId }) => toUtilityOptionValue(state, lseId))),
    };
  }, [data, knownUtilities]);

  return { states, utilityKeys, isLoading };
};

export const usePromoUtilityOptions = (states: string[], knownUtilities: PromoUtility[]) => {
  const skip = states.length === 0;
  const queryParams = useMemo(
    () =>
      skip
        ? {}
        : {
            advancedFilters: JSON.stringify([
              {
                id: 'state',
                operation: DataFilterOperation.Equal,
                selectedValues: states,
              },
            ]),
          },
    [skip, states],
  );
  const { data, isLoading } = useGetUtilitiesQuery(queryParams, { skip });

  const options = useMemo(() => {
    const byKey = new Map<string, PromoUtilityOption>();
    const add = ({ lseId, name, state }: PromoUtility) => {
      const value = toUtilityOptionValue(state, lseId);
      if (!byKey.has(value)) byKey.set(value, { label: `${name} (${state})`, value });
    };
    (data ?? []).forEach(add);
    knownUtilities.forEach(add);
    return [...byKey.values()].toSorted((a, b) => a.label.localeCompare(b.label));
  }, [data, knownUtilities]);

  // The form submits utilities grouped by state, so it needs each option's state back.
  const stateByLseId = useMemo(() => {
    const map = new Map<string, string>();
    [...knownUtilities, ...(data ?? [])].forEach(({ lseId, state }) =>
      map.set(toUtilityOptionValue(state, lseId), state),
    );
    return map;
  }, [data, knownUtilities]);

  return { options, stateByLseId, isLoading };
};
